
import { User, LogOut, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem,
  DropdownMenuSeparator, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { useProfile } from '@/hooks/profile/useProfile';

export const UserProfileDropdown = () => {
  const { user, logout, hasPermission } = useAuth();
  const { profile } = useProfile();
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Get display name - prioritize first name
  const getDisplayName = () => {
    if (!user) return 'User';
    return user.firstName || user.email || 'User';
  };

  // Get role display name with proper capitalization
  const getRoleDisplayName = () => {
    if (!user || !user.role) return '';
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  };
  
  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    await logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" aria-label="User profile">
          <Avatar className="h-8 w-8">
            <AvatarImage src={profile?.profile_image} alt="Profile" />
            <AvatarFallback className="bg-primary text-primary-foreground font-medium text-sm">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0 bg-background border shadow-lg">
        {/* User Info Header - Badge moved next to name */}
        <div className="p-4 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={profile?.profile_image} alt="Profile" />
              <AvatarFallback className="bg-primary text-primary-foreground font-medium text-lg">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col space-y-1.5 min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold leading-none truncate text-foreground">
                  {getDisplayName()}
                </p>
                <Badge variant="outline" className="px-2 py-0.5 h-5 text-xs font-normal bg-background">
                  {getRoleDisplayName()}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Menu Items - Improved spacing and hover effects */}
        <div className="p-2">
          <DropdownMenuLabel className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Account
          </DropdownMenuLabel>
          
          <DropdownMenuItem asChild className="mx-1 rounded-md">
            <Link to="/app/profile" className="cursor-pointer w-full flex items-center px-3 py-3 rounded-md hover:bg-accent transition-colors">
              <User className="mr-3 h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-foreground">My Profile</span>
            </Link>
          </DropdownMenuItem>
          
          {hasPermission('manage_roles') && (
            <DropdownMenuItem asChild className="mx-1 rounded-md">
              <Link 
                to="/app/settings" 
                onClick={() => localStorage.setItem('settingsTab', 'rbac')} 
                className="cursor-pointer w-full flex items-center px-3 py-3 rounded-md hover:bg-accent transition-colors"
              >
                <Shield className="mr-3 h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-foreground">Access Control</span>
              </Link>
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator className="my-2 mx-1" />
          
          <DropdownMenuItem 
            onClick={handleLogout}
            className="cursor-pointer text-destructive hover:text-destructive hover:bg-destructive/10 mx-1 px-3 py-3 rounded-md transition-colors"
          >
            <LogOut className="mr-3 h-4 w-4" />
            <span className="font-medium">Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
